import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, LogOut } from "lucide-react";
import ThemeToggle from "./ThemeToggle";
import AudienceSwitcher from "./AudienceSwitcher";
import BrandWordmark from "./BrandWordmark";
import { useAuth } from "../context/AuthContext";

/**
 * PublicNavBar — single source of truth for the public-facing top navigation.
 * Used on Landing, /for-students, /for-universities, /lms, /lms/labs/:id,
 * /opportunities, /login, /signup, and any other unauthenticated route.
 *
 * When a user is signed in, the "Log in" link is hidden and the primary CTA
 * becomes "Dashboard" (routing to /dashboard → role redirect). A compact
 * user chip + Log out control replaces the sign-up button.
 *
 * Props:
 *   - sticky : (default true) make the header sticky at the top.
 *   - primaryCta : { to, label } — overrides the default "Get started" button
 *                  (e.g. on /for-universities we want "Request demo")
 */
export default function PublicNavBar({ sticky = true, primaryCta }) {
  const loc = useLocation();
  const path = loc.pathname;
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const isAuthed = !!user && user !== false;

  // Close drawer whenever route changes
  useEffect(() => { setOpen(false); }, [path]);
  // Lock body scroll when drawer is open
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const linkBase = "transition hover:text-signal";
  const isActive = (m) => {
    if (Array.isArray(m)) return m.some(x => path === x);
    return typeof m === "string" ? path === m : m.test(path);
  };
  const aCls = (active) =>
    `${linkBase} ${active ? "font-semibold text-signal" : ""}`;

  const ctaTo = primaryCta?.to || "/signup";
  const ctaLabel = primaryCta?.label || "Get started";

  // Top-bar links are now just the "platform" entries — the four audience
  // pages (Students / Working Pros / Institutions / Companies) live in the
  // AudienceSwitcher strip below the header, which keeps this row breathable
  // even at narrow desktop widths and surfaces the audiences more clearly.
  const NAV_LINKS = [
    { to: "/lms",           label: "CoE Labs",      testId: "nav-lms",           match: /^\/lms/ },
    { to: "/opportunities", label: "Opportunities", testId: "nav-opportunities", match: "/opportunities" },
  ];

  return (
    <>
      <header
        className={`${sticky ? "sticky top-0 z-30" : ""} border-b bg-white border-rule`}
        data-testid="public-nav"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Link to="/" className="inline-flex items-center shrink min-w-0" data-testid="public-brand">
            <BrandWordmark to={null} size={32} testId="public-brand-wordmark" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-5 text-sm font-medium">
            {NAV_LINKS.map((l) => (
              <Link key={l.to} to={l.to} className={aCls(isActive(l.match))} data-testid={l.testId}>
                {l.label}
              </Link>
            ))}
          </nav>

          {/* Right side: theme + auth-aware CTAs + hamburger (mobile only) */}
          <div className="flex items-center gap-2 shrink-0">
            <ThemeToggle />
            {isAuthed ? (
              <>
                <Link
                  to="/dashboard"
                  className="hidden sm:inline-flex px-3 sm:px-4 py-2 rounded-md text-sm font-semibold text-white transition shadow-sm hover:shadow-md whitespace-nowrap"
                  style={{ background: "linear-gradient(135deg, #006CFF 0%, #0040A8 100%)" }}
                  data-testid="nav-dashboard-btn"
                >
                  Dashboard
                </Link>
                <button
                  type="button"
                  onClick={logout}
                  className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold text-slate-700 hover:bg-slate-50 transition"
                  data-testid="nav-logout-btn"
                  aria-label="Log out"
                >
                  <LogOut size={15} />
                  <span>Log out</span>
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="hidden sm:inline-flex px-3 sm:px-4 py-2 rounded-md text-sm font-semibold hover:bg-slate-50 transition"
                  data-testid="login-btn"
                >
                  Log in
                </Link>
                <Link
                  to={ctaTo}
                  data-testid="signup-btn"
                  className="hidden sm:inline-flex px-3 sm:px-4 py-2 rounded-md text-sm font-semibold text-white transition shadow-sm hover:shadow-md whitespace-nowrap"
                  style={{ background: "linear-gradient(135deg, #006CFF 0%, #0040A8 100%)" }}
                >
                  {ctaLabel}
                </Link>
              </>
            )}
            <button
              type="button"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              data-testid="mobile-menu-toggle"
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-md hover:bg-slate-50 transition"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </header>
      <AudienceSwitcher />

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-menu-drawer">
          {/* backdrop */}
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-black/40 backdrop-blur-[2px] transition-opacity"
          />
          {/* panel */}
          <div
            className="absolute right-0 top-0 h-full w-[86%] max-w-sm bg-white shadow-2xl flex flex-col"
            style={{ animation: "slideInFromRight 240ms ease-out" }}
          >
            <div className="px-5 py-4 flex items-center justify-between border-b border-rule">
              <Link to="/" onClick={() => setOpen(false)} className="inline-flex items-center">
                <img src="/brand/skillconnect-logo.png?v=8" alt="SkillConnect"
                  className="h-8 w-auto object-contain" />
              </Link>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                data-testid="mobile-menu-close"
                className="inline-flex items-center justify-center w-10 h-10 rounded-md hover:bg-slate-50"
              >
                <X size={22} />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto px-3 py-4" data-testid="mobile-menu-links">
              {NAV_LINKS.map((l) => {
                const active = isActive(l.match);
                return (
                  <Link
                    key={l.to}
                    to={l.to}
                    onClick={() => setOpen(false)}
                    data-testid={`m-${l.testId}`}
                    className="flex items-center justify-between px-3 py-3 rounded-md text-base font-medium hover:bg-paper transition"
                    style={active ? { color: "var(--signal)", background: "var(--signal-tint)" } : null}
                  >
                    <span>{l.label}</span>
                    {active && <span className="w-1.5 h-1.5 rounded-full bg-signal" />}
                  </Link>
                );
              })}
            </nav>

            <div className="border-t border-rule p-4 space-y-2">
              {isAuthed ? (
                <>
                  <Link
                    to="/dashboard"
                    onClick={() => setOpen(false)}
                    data-testid="m-dashboard-btn"
                    className="block w-full text-center px-4 py-3 rounded-md text-sm font-semibold text-white shadow-sm transition"
                    style={{ background: "linear-gradient(135deg, #006CFF 0%, #0040A8 100%)" }}
                  >
                    Open dashboard
                  </Link>
                  <button
                    type="button"
                    onClick={() => { setOpen(false); logout(); }}
                    data-testid="m-logout-btn"
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-md text-sm font-semibold border border-rule hover:bg-slate-50 transition"
                  >
                    <LogOut size={15} />
                    <span>Log out</span>
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setOpen(false)}
                    className="block w-full text-center px-4 py-3 rounded-md text-sm font-semibold border border-rule hover:bg-slate-50 transition"
                    data-testid="m-login-btn"
                  >
                    Log in
                  </Link>
                  <Link
                    to={ctaTo}
                    onClick={() => setOpen(false)}
                    data-testid="m-signup-btn"
                    className="block w-full text-center px-4 py-3 rounded-md text-sm font-semibold text-white shadow-sm transition"
                    style={{ background: "linear-gradient(135deg, #006CFF 0%, #0040A8 100%)" }}
                  >
                    {ctaLabel}
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
